<script>
/**
 * @typedef { import('vue').Ref } Ref
 */

/**
 * @typedef {?Object} RateObject
 * @property {string} currency - Currency for rates, e.g., 'EUR'.
 * @property {number} monthly_rate - Expected monthly rate.
 * @property {number} hourly_rate - Expected hourly rate.
 * @property {number} gig_overtime_hourly_rate - Expected gig/overtime hourly rate.
 * @property {?string|number} monthly_rate_updated - Updated value of monthly rate.
 * @property {?string|number} hourly_rate_updated - Updated value of hourly rate.
 * @property {?string|number} gig_overtime_hourly_rate_updated - Updated value of gig/overtime hourly rate.
 * */

/**
 * @typedef {Object} OpportunityData
 * @property {('very_high'|'high'|'medium'|'low'|'very_low'|'unclassified')} value - Opportunity level key.
 * @property {string} label - Human-readable label, e.g. 'Very high opportunity'.
 */

/**
 * @typedef {Object} SegmentData
 * @property {string} role - Segment role, e.g. 'Frontend Engineer'.
 * @property {string} primary_skill - Primary skill of the segment.
 * @property {?string} second_primary_skill - Optional secondary skill of the segment.
 * @property {string} region - Region of the segment, e.g. 'Europe'.
 * @property {?string} summary - Human-readable explanation of the opportunity score.
 * @property {OpportunityData} opportunity - Opportunity score and label for this segment.
 */

/**
 * Represents the profile of a job applicant, including personal, professional, and application-specific details.
 *
 * @typedef {?Object} ApplicantProfile
 * @property {number} id - Unique identifier for the applicant.
 * @property {string} full_name - Full name of the applicant.
 * @property {?number} main_application_id - The ID of the main application, if applicable.
 * @property {string} first_name - Applicant's first name.
 * @property {string} last_name - Applicant's last name.
 * @property {string} email - Email address of the applicant.
 * @property {?string} phone - Phone number of the applicant, if available.
 * @property {number} average_rating - The average rating of the applicant.
 * @property {string} city - City of residence.
 * @property {string} timezone - Timezone of the applicant's location.
 * @property {number} relocate - Indicates if the applicant is willing to relocate (0 for no, 1 for yes).
 * @property {string} competency - Area of competency, e.g., 'Frontend'.
 * @property {string} country - Country of residence.
 * @property {string} country_code - Two-letter country code.
 * @property {number} commercial_exp_months - Total months of commercial experience.
 * @property {number} freelance_exp_months - Total months of freelance experience.
 * @property {string} commercial_experience - Human-readable commercial experience duration.
 * @property {string} freelance_experience - Human-readable freelance experience duration.
 * @property {number} english_level - English proficiency level of the applicant.
 * @property {?number} english_cefr_score - CEFR score for English proficiency, if available.
 * @property {string} commitment_type - Types of commitment the applicant is open to, e.g., Full-time, Part-time.
 * @property {number} hiring_probability - Probability of hiring the applicant.
 * @property {number} profile_probability - Probability of the profile being a good fit.
 * @property {string} applied_at - Date and time when the applicant applied.
 * @property {?string} project_interested - Project(s) the applicant is interested in, if any.
 * @property {?number} team_size - Preferred team size, if specified.
 * @property {number} want_to_fulltime - Indicates if the applicant wants a full-time position (0 for no, 1 for yes).
 * @property {string} first_start_fulltime - Availability date for starting full-time.
 * @property {string} first_start_parttime - Availability date for starting part-time.
 * @property {string} source - Source of the application, e.g., 'applied'.
 * @property {?string} campaign - Campaign the application came from, if any.
 * @property {?number} referrer_id - Referrer ID, if applicable.
 * @property {?string} referrer_email - Referrer email, if applicable.
 * @property {?string} recruitment_link - Link to the related staffing request, if applicable.
 * @property {Object} social_links - Social media links of the applicant.
 * @property {Object} stage - Current stage of the application process.
 * @property {Object[]} application_skills - Array of skills with details related to the application. Each skill may include a `segment` property of type {@link SegmentData}.
 * @property {string} last_activity - Date and time of the last activity by the applicant.
 * @property {boolean} is_rejected - Indicates if the applicant has been rejected.
 * @property {?string} rejected_at - Date and time when the applicant was rejected, if applicable.
 * @property {string[]} rejection_reasons - Reasons for rejection, if any.
 * @property {?string} rejection_type - Type of rejection, if applicable.
 * @property {number} is_hired - Indicates if the applicant has been hired (0 for no, 1 for yes).
 * @property {?string} hired_at - Hiring date, if applicable.
 * @property {boolean} fast_tracked - Indicates if the applicant is in the fast-track process.
 * @property {string} fast_track_choice - Fast track process choice.
 * @property {boolean} interview_is_filled - Indicates if the interview slot has been filled.
 * @property {?string} demand_level - Demand level of the applicant
 * @property {?('opportunity_score'|'demand_level')} probability_based_on - Which scoring model is active for this application.
 * @property {?{segment: SegmentData}} current_segment - The matched segment for this application at scoring time.
 * @property {boolean} has_not_scheduled_meeting - Indicates if the applicant has not scheduled a meeting yet.
 * @property {?number} cv_matching_score - CV matching score, if calculated.
 * @property {number} priority - Priority level of the application.
 * @property {string} urgency - Urgency of the application, e.g., 'critical'.
 * @property {?string} desired_test_date - Desired date for taking a test, if specified.
 * @property {?string} auto_transition - Auto transition status, if applicable.
 * @property {?Object} applicant - Applicant details, if available.
 * @property {Object} assignees - Details of individuals assigned to the application.
 * @property {?Object} rejected_by - Details of the individual who rejected the application, if applicable.
 * @property {Object[]} attachments - Array of attachments related to the application.
 * @property {Object[]} recordings - Array of recordings related to the application.
 * @property {Object} interviewers - Details of interviewers assigned to the application.
 * @property {Object} duplications - Details of any duplicate applications.
 * @property {?number} grade - Grade of the applicant, if applicable.
 * @property {Object} metric_distribution - Distribution of various metrics related to the application.
 * @property {?RateObject} rate - Details of rate related values.
 * @property {?(string|('monthly'|'hourly'))} compensation_plan - Represents compensation plan of the applicant.
 * @property {?{id: number, label: string, name: string}} vetting_plan - Vetting plan the application follows, if any.
 * @property {boolean} eligible_to_change_vetting_plan - Indicates if the vetting plan of the application can still be changed.
 */

export default {
  name: 'ApplicationForm',
};
</script>
<script setup>
import { useStore } from 'vuex';
import { useForm } from 'vee-validate';
import { useConfirmDialog } from '@vueuse/core';
import { computed, ref, toRefs, watch } from 'vue';
import PDialog from '@/components/ProxifyUI/PDialog.vue';
import PSeparator from '@/components/ProxifyUI/PSeparator.vue';
import BaseAlert from '@/components/Base/BaseAlert.vue';
import BaseBadge from '@/components/Base/BaseBadge.vue';
import BaseConfirmationCard from '@/components/Base/BaseConfirmationCard.vue';
import ApplicationFormSectionSource from '@/components/Elements/Application/ApplicationFormSectionSource.vue';
import ApplicationFormSectionLinks from '@/components/Elements/Application/ApplicationFormSectionLinks.vue';
import ApplicationFormSectionAdditionalInformation from '@/components/Elements/Application/ApplicationFormSectionAdditionalInformation.vue';
import ApplicationFormSectionExperience from '@/components/Elements/Application/ApplicationFormSectionExperience.vue';
import ApplicationFormSectionPersonalInformation from '@/components/Elements/Application/ApplicationFormSectionPersonalInformation.vue';
import ApplicationDuplicateModal from './ApplicationDuplicateModal.vue';
import api from '@/api';

const props = defineProps({
  initialData: {
    /** @type {{ new (): ApplicantProfile }} */
    type: Object,
    required: false,
    default() {
      return {};
    },
  },
  onSubmit: {
    type: Function,
    required: true,
  },
  uploadProgress: {
    type: Number,
    required: false,
    default() {
      return 100;
    },
  },
  title: {
    type: String,
    default() {
      return 'Add new applicant';
    },
  },
  previousStageName: {
    type: String,
    required: false,
    default() {
      return '';
    },
  },
  nextStageName: {
    type: String,
    required: false,
    default() {
      return '';
    },
  },
});
const emit = defineEmits(['cancel']);
const store = useStore();
const { reveal, isRevealed, cancel, onCancel, confirm } = useConfirmDialog();

/** @type {Ref<boolean>} */
const isLoading = ref(false);
/** @type {Ref<ApplicantProfile>} */
const form = ref({
  attachments: {},
  rate: {},
});

const isDuplicateDialogActive = ref(false);
const duplicateResponse = ref([]);

const { initialData, previousStageName, nextStageName } = toRefs(props);

const isInSourcingStage = computed(() => initialData.value.stage?.is_sourcing);
const isEditing = computed(() => Boolean(initialData.value.id));
const isVettingPlanVisible = computed(
  () => !isEditing.value || Boolean(initialData.value.vetting_plan)
);
const isVettingPlanDisabled = computed(
  () =>
    isVettingPlanVisible.value &&
    isEditing.value &&
    !initialData.value.eligible_to_change_vetting_plan
);
const isVettingPlanRequired = computed(
  () => isVettingPlanVisible.value && !isVettingPlanDisabled.value
);
// An existing application already linked to a referrer keeps that referrer,
// so the referral e-mail only has to be filled in when there is none.
const isReferrerLinked = computed(
  () => isEditing.value && Boolean(initialData.value.referrer_id)
);
const allowNameOnlySkills = computed(() => {
  return (
    (!isEditing.value || isInSourcingStage.value) &&
    form.value.source === 'sourced' &&
    (!nextStageName.value || nextStageName.value.includes('Sourcing'))
  );
});

const isRateGuidanceEnabled = computed(() => {
  if (initialData.value.compensation_plan) {
    return initialData.value.compensation_plan === 'monthly';
  }
  return import.meta.env.VITE_RATE_GUIDANCE_ENABLED === 'true';
});

const requiredFields = computed(() => {
  const alwaysRequired = [
    'First name',
    'Last name',
    'Country',
    'Source',
    'Sourcer',
    'Linkedin',
    'CV File',
    'Referral e-mail',
    'Vetting plan',
  ];
  const requiredForVetting = [
    'Main skills',
    'E-mail',
    'Commercial experience',
    'English level',
    'Competency',
    'Hourly rate',
    'Current availability',
    ...(isRateGuidanceEnabled.value ? ['Full-time monthly rate'] : []),
  ];
  const requiredForNonSourced = [
    'Main skills',
    'E-mail',
    'Commercial experience',
    'English level',
    'Competency',
  ];
  const isTransitioning = Boolean(
    previousStageName.value && nextStageName.value
  );
  return form.value.source
    ? [
        ...alwaysRequired,
        ...(form.value.source !== 'sourced' ? requiredForNonSourced : []),
        ...((form.value.source !== 'sourced' && !isEditing.value) ||
        (isEditing.value && (!isInSourcingStage.value || isTransitioning))
          ? requiredForVetting
          : []),
      ]
    : ['Source'];
});

const schema = computed(() => {
  const validSkillCondition =
    (isEditing.value && !isInSourcingStage.value) ||
    form.value.source !== 'sourced' ||
    (Boolean(nextStageName.value) && !nextStageName.value.includes('Sourcing'));
  return isRevealed.value
    ? requiredFields.value.reduce(
        (accumulator, name) => {
          return {
            [name]: {
              required: !(
                (name === 'CV File' && form.value?.linkedin_link) ||
                (name === 'Linkedin' && form.value?.attachments?.cv_file) ||
                (name === 'Sourcer' && form.value?.source !== 'sourced') ||
                (name === 'Referral e-mail' &&
                  (form.value?.source !== 'referred' ||
                    isReferrerLinked.value)) ||
                (name === 'Vetting plan' && !isVettingPlanRequired.value)
              ),
              ...(name === 'E-mail' ? { email: true } : {}),
            },
            ...accumulator,
          };
        },
        {
          'Main skills': {
            required: requiredFields.value.includes('Main skills'),
            validSkill: validSkillCondition,
          },
          'Secondary Skills': {
            required: requiredFields.value.includes('Secondary Skills'),
            validSkill: validSkillCondition,
          },
        }
      )
    : {};
});

const { handleSubmit, resetForm, values, setFieldError } = useForm({
  validationSchema: schema,
});
const save = async () => {
  if (isLoading.value) return;
  try {
    isLoading.value = true;
    form.value.id = initialData.value.id;
    await handleCheckDuplicates(form.value);
  } catch (error) {
    const recruitmentLinkErrors = error.data?.errors?.recruitment_link;
    if (recruitmentLinkErrors) {
      setFieldError('Related staffing request', recruitmentLinkErrors[0]);
    }
  } finally {
    isLoading.value = false;
  }
};

const handleInvalidSubmit = ({ errors }) => {
  store.commit('ui/addSnackbarMessage', {
    title: 'You have to fill all the required fields',
    type: 'error',
  });
  const invalidFields = Object.keys(errors);
  const firstInvalidQuestionElement = document.querySelector(
    `[data-name='${invalidFields[0]}']`
  );
  if (firstInvalidQuestionElement) {
    firstInvalidQuestionElement.scrollIntoView();
  }
};

const handleCheckDuplicates = async (form) => {
  const { onSubmit } = props;
  const shouldSkipDuplicateCheck = isEditing.value && !isInSourcingStage.value;
  if (shouldSkipDuplicateCheck) {
    await onSubmit(form);
    confirm();
    return;
  }
  const { data } = await api.applications.checkDuplicates(form);
  if (data.response.length) {
    duplicateResponse.value = data.response;
    isDuplicateDialogActive.value = true;
  } else {
    await onSubmit(form);
    confirm();
  }
};

const confirmDuplicateSubmission = async () => {
  isDuplicateDialogActive.value = false;
  const { onSubmit } = props;
  try {
    isLoading.value = true;
    await onSubmit(form.value);
    confirm();
  } finally {
    isLoading.value = false;
  }
};

const submit = handleSubmit(save, handleInvalidSubmit);

watch(
  () => form.value?.source,
  (newValue, oldValue) => {
    if (oldValue && newValue === oldValue) return;
    resetForm({
      values: {
        ...values,
        Source: newValue,
      },
    });
  },
  {
    deep: true,
  }
);

onCancel(() => {
  form.value = {
    attachments: {},
    rate: {},
  };
  emit('cancel');
});

defineExpose({
  reveal,
  submit,
});
</script>

<template>
  <PDialog
    v-model="isRevealed"
    persistent
    position="standard"
  >
    <BaseConfirmationCard
      :cancel-button-text="null"
      confirm-button-text="Submit"
      :is-loading="isLoading"
      :on-confirm="submit"
      :on-close="cancel"
      class="!max-w-[640px] w-full"
      width="640px"
      paddingless
      footer-border
    >
      <template #title>
        <div class="flex gap-2">
          <span>{{ title }}</span>
          <BaseBadge
            v-if="isRateGuidanceEnabled"
            color="warning"
            label="Monthly Comp Member"
          ></BaseBadge>
        </div>
      </template>
      <form
        class="px-6 pb-6 flex flex-col gap-4"
        @submit="submit"
      >
        <BaseAlert
          v-if="previousStageName && nextStageName"
          color="warning"
          hide-close-button
          vertical
        >
          <div class="text-body-sm">
            Fill required applicant information to be able to change the stage:
          </div>
          <div class="text-body-md font-medium">
            {{ previousStageName }} → {{ nextStageName }}
          </div>
        </BaseAlert>
        <ApplicationFormSectionSource
          :referrer-email="initialData.referrer_email"
          :referrer-id="initialData.referrer_id"
          :recruitment-link="initialData.recruitment_link"
          :source="initialData.source"
          :sourcer-id="initialData.assignees?.sourcer?.id"
          :is-editing="isEditing"
          :required-fields="requiredFields"
          :vetting-plan-id="initialData.vetting_plan?.id"
          :vetting-plan-visible="isVettingPlanVisible"
          :vetting-plan-disabled="isVettingPlanDisabled"
          @update:referrer-email="(value) => (form.referrer_email = value)"
          @update:recruitment-link="(value) => (form.recruitment_link = value)"
          @update:source="(value) => (form.source = value)"
          @update:sourcer-id="(value) => (form.sourcer_id = value)"
          @update:vetting-plan-id="(value) => (form.vetting_plan_id = value)"
        />
        <PSeparator class="mt-2" />
        <ApplicationFormSectionPersonalInformation
          :city="initialData.city"
          :country="initialData.country"
          :email="initialData.email"
          :first-name="initialData.first_name"
          :last-name="initialData.last_name"
          :phone="initialData.phone"
          :relocate="Boolean(initialData.relocate)"
          :required-fields="requiredFields"
          @update:city="(value) => (form.city = value)"
          @update:country="(value) => (form.country = value)"
          @update:email="(value) => (form.email = value)"
          @update:first-name="(value) => (form.first_name = value)"
          @update:last-name="(value) => (form.last_name = value)"
          @update:phone="(value) => (form.phone = value)"
          @update:relocate="(value) => (form.relocate = value)"
        />
        <PSeparator class="mb-2" />
        <ApplicationFormSectionExperience
          :commercial-experience-months="initialData.commercial_exp_months"
          :competency="initialData.competency"
          :english-level="initialData.english_level"
          :freelance-experience-months="initialData.freelance_exp_months"
          :application-skills="initialData.application_skills"
          :required-fields="requiredFields"
          :allow-name-only-skills="allowNameOnlySkills"
          :source="form.source"
          :is-editing="isEditing"
          @update:commercial-experience-months="
            (value) => (form.commercial_exp_months = value)
          "
          @update:competency="(value) => (form.competency = value)"
          @update:english-level="(value) => (form.english_level = value)"
          @update:freelance-experience-months="
            (value) => (form.freelance_exp_months = value)
          "
          @update:application-skills="(value) => (form.skills = value)"
        />
        <PSeparator class="mb-2" />
        <ApplicationFormSectionAdditionalInformation
          :commitment-type="initialData.commitment_type"
          :currency="initialData.rate?.currency"
          :hourly-rate="initialData.rate?.hourly_rate"
          :hourly-rate-updated="initialData.rate?.hourly_rate_updated"
          :gig-overtime-hourly-rate="initialData.rate?.gig_overtime_hourly_rate"
          :gig-overtime-hourly-rate-updated="
            initialData.rate?.gig_overtime_hourly_rate_updated
          "
          :monthly-rate="initialData.rate?.monthly_rate"
          :monthly-rate-updated="initialData.rate?.monthly_rate_updated"
          :project-interested="initialData.project_interested"
          :required-fields="requiredFields"
          :team-size="initialData.team_size"
          :want-to-full-time="Boolean(initialData.want_to_fulltime)"
          :is-rate-guidance-enabled="isRateGuidanceEnabled"
          @update:commitment-type="(value) => (form.work_type = value)"
          @update:currency="(value) => (form.rate.currency = value)"
          @update:hourly-rate="(value) => (form.rate.hourly_rate = value)"
          @update:hourly-rate-updated="
            (value) => (form.rate.hourly_rate_updated = value)
          "
          @update:gig-overtime-hourly-rate="
            (value) => (form.rate.gig_overtime_hourly_rate = value)
          "
          @update:gig-overtime-hourly-rate-updated="
            (value) => (form.rate.gig_overtime_hourly_rate_updated = value)
          "
          @update:monthly-rate="(value) => (form.rate.monthly_rate = value)"
          @update:monthly-rate-updated="
            (value) => (form.rate.monthly_rate_updated = value)
          "
          @update:project-interested="
            (value) => (form.project_interested = value)
          "
          @update:team-size="(value) => (form.team_size = value)"
          @update:want-to-full-time="(value) => (form.ramp_up = value)"
        />
        <PSeparator />
        <ApplicationFormSectionLinks
          :cv-file="
            (initialData.attachments ?? []).find(
              ({ type }) => type === 'cv_file'
            )
          "
          :designer-link="initialData.social_links?.designer_link"
          :github-link="initialData.social_links?.github_link"
          :kaggle-link="initialData.social_links?.kaggle_link"
          :linkedin-link="initialData.social_links?.linkedin_link"
          :other-file="
            (initialData.attachments ?? []).find(
              ({ type }) => type === 'other_file'
            )
          "
          :other-link="initialData.social_links?.other_link"
          :required-fields="requiredFields"
          :upload-progress="uploadProgress"
          @update:cv-file="
            (value) =>
              (form = {
                ...form,
                attachments: { ...form.attachments, cv_file: value },
              })
          "
          @update:designer-link="(value) => (form.designer_link = value)"
          @update:github-link="(value) => (form.github_link = value)"
          @update:kaggle-link="(value) => (form.kaggle_link = value)"
          @update:linkedin-link="(value) => (form.linkedin_link = value)"
          @update:other-file="
            (value) =>
              (form = {
                ...form,
                attachments: { ...form.attachments, other_file: value },
              })
          "
          @update:other-link="(value) => (form.other_link = value)"
        />
      </form>
    </BaseConfirmationCard>
    <ApplicationDuplicateModal
      :model-value="isDuplicateDialogActive"
      :duplicates="duplicateResponse"
      :is-editing="isEditing"
      @update:model-value="isDuplicateDialogActive = $event"
      @confirm="confirmDuplicateSubmission"
      @close="isDuplicateDialogActive = false"
    />
  </PDialog>
</template>
